import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database as SupabaseDatabase } from './supabase-types';
import { OFFLINE_TEST_SUPABASE_URL, OFFLINE_TEST_SUPABASE_ANON_KEY, offlineTestClientOptions } from './supabaseTestOffline';

// Get environment variables - FAIL FAST if not set
// Trim to remove any trailing whitespace/newlines from .env files
const runtimeHostname = typeof window !== 'undefined' ? window.location.hostname : '';
// A plain Node script with no `window` (e.g. scripts/render-rtw-proposal-fixture.tsx
// run under tsx) gets the same local fallback as a browser on localhost, but only
// outside production builds. The fallback key is the public anon key already
// shipped in every browser bundle; access is gated by RLS, not by secrecy.
// `process` only exists under Node; a Vite browser bundle never defines it, and the
// browser tsconfig has no Node types, so read it off globalThis with a local shape.
const nodeEnv = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env?.NODE_ENV;
const isNodeScript = typeof window === 'undefined'
  && !import.meta.env?.PROD
  && nodeEnv !== 'production'
  && nodeEnv !== 'staging';
const isLocalRuntime = isNodeScript || ['localhost', '127.0.0.1', '::1'].includes(runtimeHostname);
const isStaging = typeof window !== 'undefined' && (runtimeHostname.includes('staging') || runtimeHostname.includes('preview'));

// Get environment variables - FAIL FAST if not set
// Trim to remove any trailing whitespace/newlines from .env files
// `import.meta.env` only exists under Vite's transform; optional-chain so a plain
// Node import reaches the fail-fast check below instead of throwing here.
let supabaseUrl = (import.meta.env?.VITE_SUPABASE_URL || '').trim();
let supabaseAnonKey = (import.meta.env?.VITE_SUPABASE_ANON_KEY || '').trim();

// Under vitest with nothing configured the client is OFFLINE: it must never reach production
// (see supabaseTestOffline.ts). `MODE` is the constant "production"/"development" in a real
// build, so this is dead code outside tests.
const useOfflineTestClient = import.meta.env?.MODE === 'test' && (!supabaseUrl || !supabaseAnonKey);
if (useOfflineTestClient) {
  supabaseUrl = OFFLINE_TEST_SUPABASE_URL;
  supabaseAnonKey = OFFLINE_TEST_SUPABASE_ANON_KEY;
}

// Local Codex/Vite previews often run from ignored worktrees without an .env.local.
// Keep production fail-fast behavior, but avoid a blank localhost app when env is absent.
if (isLocalRuntime && (!supabaseUrl || !supabaseAnonKey)) {
  supabaseUrl = 'https://xhuybqjcocnoekmqyvxm.supabase.co';
  supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhodXlicWpjb2Nub2VrbXF5dnhtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjE3NTEzMzMsImV4cCI6MjA3NzMyNzMzM30.oBgwo_j3Dv97-c2mctQ5o5oqETWKtCDHvgAprQ2sRT0';
}

// Force Staging Credentials if on Staging Domain (Runtime Override)
if (isStaging && !useOfflineTestClient) {
  supabaseUrl = 'https://xgytoxkexzvpsladqovg.supabase.co';
  supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhneXRveGtleHp2cHNsYWRxb3ZnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njk2OTU1NjMsImV4cCI6MjA4NTI3MTU2M30.mwz8B4mkOj_zny_UyIlfQzP569Z0C6vU7-cBzZio2_8';
}


// Validate required environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing required Supabase environment variables. ' +
    'Please ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set in your .env file.'
  );
}

// Supabase generated types in this repo omit `Relationships` on table definitions.
// Some supabase-js typings require it to correctly infer Insert/Update payload types.
// This patched Database type adds `Relationships: []` to all tables to avoid `never` Insert/Update.
type AddRelationships<T> = T extends { Row: unknown; Insert: unknown; Update: unknown }
  ? T & { Relationships: [] }
  : T;

type PatchSchema<S> = S extends { Tables: infer T }
  ? Omit<S, 'Tables'> & { Tables: { [K in keyof T]: AddRelationships<T[K]> } }
  : S;

export type Database = Omit<SupabaseDatabase, 'public'> & {
  public: PatchSchema<SupabaseDatabase['public']>;
};

// The generated Supabase type map in this workspace regularly trails schema/RPC changes.
// We intentionally expose a permissive runtime client type for app-wide calls so schema drift
// does not block `tsc` across unrelated modules. Strict database types remain exported above.
type RuntimeDatabase = any;
type RuntimeClient = SupabaseClient<any, 'public', 'public', any, { PostgrestVersion: '12' }>;

// ============================================================================
// LAZY INITIALIZATION PATTERN
// To prevent Temporal Dead Zone issues during bundler module processing,
// we use a lazy singleton pattern for the supabase client.
// ============================================================================

let _supabaseInstance: RuntimeClient | null = null;

function getSupabaseClient(): RuntimeClient {
  if (!_supabaseInstance) {
    _supabaseInstance = createClient<RuntimeDatabase>(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: false,
      },
      ...(useOfflineTestClient ? offlineTestClientOptions : {}),
    }) as unknown as RuntimeClient;
  }
  return _supabaseInstance as RuntimeClient;
}

// Export a proxy that lazily initializes the client on first access
// This prevents TDZ issues by deferring createClient until runtime
export const supabase = new Proxy({} as RuntimeClient, {
  get(_target, prop) {
    return Reflect.get(getSupabaseClient(), prop);
  }
});

// Export the URL and key for use in edge function calls
export const getSupabaseUrl = () => supabaseUrl;
export const getSupabaseAnonKey = () => supabaseAnonKey;

export type { SupabaseDatabase };
